import { useState, useEffect, useCallback, useMemo } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Separator } from '@/components/ui/separator';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { MonthYearPicker } from '@/components/seek/profile/MonthYearPicker';
import { nationalities } from '@/data/nationalities';
import type { ReviewDecisions, DecisionAction, FieldDecision, ItemDecision, AdditionalSectionDecision } from '@/hooks/useReviewSession';
import { useTranslation } from 'react-i18next';
import {
  CheckCircle2, AlertTriangle, Info, Shield, Briefcase,
  GraduationCap, Wrench, Globe, Award, Loader2, ChevronDown, ChevronUp, Pencil,
  User, MapPin, Flag, BookOpen, Clock, Search, Check, ChevronsUpDown,
  FileQuestion, ListChecks, ThumbsUp, ThumbsDown, RotateCcw, Sparkles
} from 'lucide-react';
import type { Tables } from '@/integrations/supabase/types';

type ProfileDecisionKey = Exclude<keyof ReviewDecisions,
  'experiences' | 'education' | 'additional_sections'>;

interface ParsedData {
  extraction_quality: string;
  confidence?: Record<string, string>;
  headline?: string | null;
  professional_summary?: string | null;
  current_job_title?: string | null;
  total_years_experience?: number | null;
  phone?: string | null;
  city?: string | null;
  country?: string | null;
  nationality?: string | null;
  highest_education_level?: string | null;
  experiences?: Array<{
    title: string;
    employer_name?: string | null;
    location?: string | null;
    employment_type?: string | null;
    start_date?: string | null;
    end_date?: string | null;
    is_current?: boolean | null;
    description?: string | null;
  }>;
  education?: Array<{
    institution_name: string;
    degree?: string | null;
    field_of_study?: string | null;
    start_date?: string | null;
    end_date?: string | null;
  }>;
  skills?: string[] | { skills?: string[]; expertise?: string[]; key_strengths?: string[] };
  certifications?: Array<{
    certification_name: string;
    issuing_organization?: string | null;
  }>;
  languages?: Array<{
    language_name: string;
    proficiency_level?: string | null;
  }>;
  additional_sections?: Array<{
    stable_id: string;
    section_label: string;
    category: string;
    content: string;
    confidence: number;
    quality: string;
  }>;
}

interface ValidationIssue {
  section: string;
  field?: string;
  issue: string;
  severity: 'error' | 'warning' | 'info';
  auto_fixed?: boolean;
}

interface CurrentData {
  profile: Pick<Tables<'candidate_profiles'>,
    'current_job_title' | 'headline' | 'city' | 'country' | 'highest_education_level' |
    'total_years_experience' | 'professional_summary'> | null;
  experiences: unknown[];
  education: unknown[];
  skills: unknown[];
  languages: unknown[];
  certifications: unknown[];
}

interface Props {
  open: boolean;
  onClose: () => void;
  onDiscard: () => void;
  onApply: (editedData: ParsedData, decisions: ReviewDecisions) => void;
  onFinishNoChanges?: () => void;
  applying: boolean;
  parsedData: ParsedData;
  currentData: CurrentData;
  validationIssues: ValidationIssue[];
  extractionQuality: string;
  confidence: Record<string, string>;
  // Decision-control props (Phase 2)
  decisions: ReviewDecisions;
  onUpdateFieldAction: (field: string, action: DecisionAction) => void;
  onUpdateItemAction: (section: 'experiences' | 'education', index: number, action: DecisionAction) => void;
  onUpdateAdditionalAction: (stableId: string, action: DecisionAction) => void;
  isRecoveredSession?: boolean;
}

/* ── Decision toggle button group ── */

function DecisionToggle({ action, onChange, compact }: {
  action: DecisionAction;
  onChange: (a: DecisionAction) => void;
  compact?: boolean;
}) {
  const { t } = useTranslation();
  const size = compact ? 'h-6 px-1.5 text-[10px]' : 'h-7 px-2 text-xs';
  return (
    <div className="flex items-center gap-0.5 bg-muted/60 rounded-lg p-0.5">
      <button
        type="button"
        className={`rounded-md transition-colors flex items-center gap-1 font-medium ${size} ${
          action === 'accept'
            ? 'bg-seek/15 text-seek shadow-sm'
            : 'text-muted-foreground hover:text-foreground hover:bg-background/60'
        }`}
        onClick={() => onChange('accept')}
        title={t('seek.cvDiff.useCvValue')}
      >
        <ThumbsUp className="w-3 h-3" />
        {!compact && t('seek.cvDiff.accept')}
      </button>
      <button
        type="button"
        className={`rounded-md transition-colors flex items-center gap-1 font-medium ${size} ${
          action === 'keep_current'
            ? 'bg-blue-500/15 text-blue-700 dark:text-blue-300 shadow-sm'
            : 'text-muted-foreground hover:text-foreground hover:bg-background/60'
        }`}
        onClick={() => onChange('keep_current')}
        title={t('seek.cvDiff.keepCurrentProfileValue')}
      >
        <RotateCcw className="w-3 h-3" />
        {!compact && t('seek.cvDiff.keep')}
      </button>
      <button
        type="button"
        className={`rounded-md transition-colors flex items-center gap-1 font-medium ${size} ${
          action === 'reject'
            ? 'bg-destructive/15 text-destructive shadow-sm'
            : 'text-muted-foreground hover:text-foreground hover:bg-background/60'
        }`}
        onClick={() => onChange('reject')}
        title={t('seek.cvDiff.rejectThisValue')}
      >
        <ThumbsDown className="w-3 h-3" />
        {!compact && t('seek.cvDiff.reject')}
      </button>
    </div>
  );
}

/* ── Tiny reusable badges ── */

function ConfidenceBadge({ level }: { level: string }) {
  const { t } = useTranslation();
  const map: Record<string, { cls: string; label: string }> = {
    high:   { cls: 'bg-seek/10 text-seek border-seek/20', label: t('seek.cvDiff.confidence.high') },
    medium: { cls: 'bg-amber-500/10 text-amber-700 border-amber-200',     label: t('seek.cvDiff.confidence.medium') },
    low:    { cls: 'bg-red-500/10 text-red-700 border-red-200',           label: t('seek.cvDiff.confidence.low') },
  };
  const c = map[level] || map.medium;
  return <Badge variant="outline" className={`text-[10px] leading-none px-1.5 py-0.5 ${c.cls}`}>{c.label}</Badge>;
}

function QualityBadge({ quality }: { quality: string }) {
  const { t } = useTranslation();
  const map: Record<string, { cls: string; icon: typeof CheckCircle2 }> = {
    excellent: { cls: 'bg-seek/10 text-seek border-seek/30', icon: CheckCircle2 },
    good:      { cls: 'bg-blue-500/10 text-blue-700 border-blue-300',        icon: CheckCircle2 },
    fair:      { cls: 'bg-amber-500/10 text-amber-700 border-amber-300',     icon: AlertTriangle },
    poor:      { cls: 'bg-red-500/10 text-red-700 border-red-300',           icon: AlertTriangle },
  };
  const c = map[quality] || map.fair;
  const qualityKey = `seek.cvDiff.quality.${quality}`;
  const qualityLabel = t(qualityKey);
  const Icon = c.icon;
  return (
    <Badge variant="outline" className={`gap-1 text-xs ${c.cls}`}>
      <Icon className="w-3 h-3" />
      {qualityLabel === qualityKey ? quality : qualityLabel}
    </Badge>
  );
}

/* ── Editable field row with decision ── */

function EditableFieldWithDecision({ label, cvValue, currentValue, onChange, icon: Icon, decision, onDecisionChange }: {
  label: string;
  cvValue: string;
  currentValue?: string;
  onChange: (v: string) => void;
  icon?: typeof Briefcase;
  decision?: FieldDecision;
  onDecisionChange?: (action: DecisionAction) => void;
}) {
  const { t } = useTranslation();
  const [editing, setEditing] = useState(false);
  if (!cvValue && cvValue !== '') return null;

  const hasConflict = currentValue && currentValue !== '' && currentValue !== cvValue;
  const displayValue = decision?.action === 'keep_current' && currentValue ? currentValue : cvValue;

  return (
    <div className="py-1.5 space-y-1">
      <div className="flex items-center justify-between gap-2">
        <span className="text-xs text-muted-foreground font-medium flex items-center gap-1.5 truncate">
          {Icon && <Icon className="w-3.5 h-3.5 shrink-0 text-muted-foreground/60" />}
          {label}
        </span>
        {decision && onDecisionChange && hasConflict && (
          <DecisionToggle action={decision.action} onChange={onDecisionChange} compact />
        )}
      </div>
      {hasConflict && decision?.action !== 'reject' && (
        <div className="grid grid-cols-2 gap-2 text-xs">
          <div className={`rounded-md p-1.5 border ${decision?.action === 'keep_current' ? 'border-blue-300 bg-blue-50 dark:bg-blue-950/20' : 'border-border/40 bg-muted/20 opacity-60'}`}>
            <span className="text-[10px] font-medium text-muted-foreground block mb-0.5">{t('seek.cvDiff.current')}</span>
            <span className="text-foreground">{currentValue || '—'}</span>
          </div>
          <div className={`rounded-md p-1.5 border ${decision?.action === 'accept' ? 'border-seek/30 bg-seek/5' : 'border-border/40 bg-muted/20 opacity-60'}`}>
            <span className="text-[10px] font-medium text-muted-foreground block mb-0.5">{t('seek.cvDiff.fromCv')}</span>
            {editing ? (
              <Input
                value={cvValue}
                onChange={(e) => onChange(e.target.value)}
                onBlur={() => setEditing(false)}
                onKeyDown={(e) => e.key === 'Enter' && setEditing(false)}
                className="h-6 text-xs p-1"
                autoFocus
              />
            ) : (
              <span
                className="text-foreground cursor-pointer hover:text-seek flex items-center gap-1"
                onClick={() => setEditing(true)}
              >
                {cvValue || '—'}
                <Pencil className="w-2.5 h-2.5 opacity-0 group-hover:opacity-100" />
              </span>
            )}
          </div>
        </div>
      )}
      {(!hasConflict || decision?.action === 'reject') && (
        <div className="flex items-center gap-1.5 min-w-0">
          {decision?.action === 'reject' ? (
            <span className="text-xs text-muted-foreground line-through">{cvValue}</span>
          ) : editing ? (
            <Input
              value={displayValue}
              onChange={(e) => onChange(e.target.value)}
              onBlur={() => setEditing(false)}
              onKeyDown={(e) => e.key === 'Enter' && setEditing(false)}
              className="h-8 text-sm"
              autoFocus
            />
          ) : (
            <div
              className="flex items-center gap-1.5 min-w-0 cursor-pointer rounded-md px-2 py-1 -mx-2 hover:bg-seek/5 transition-colors group"
              onClick={() => setEditing(true)}
            >
              <span className="text-sm text-foreground truncate">{displayValue || '—'}</span>
              <Pencil className="w-3 h-3 text-muted-foreground/30 opacity-0 group-hover:opacity-100 shrink-0 transition-opacity" />
            </div>
          )}
        </div>
      )}
    </div>
  );
}

/* ── Editable select field with search + free text ── */

function EditableNationalityField({ label, value, onChange, icon: Icon }: {
  label: string;
  value: string;
  onChange: (v: string) => void;
  icon?: typeof Briefcase;
}) {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState('');

  const filtered = useMemo(() => {
    if (!search.trim()) return nationalities;
    const q = search.toLowerCase();
    return nationalities.filter((n) => n.toLowerCase().includes(q));
  }, [search]);

  const showFreeText = search.trim().length > 0 && !nationalities.some(n => n.toLowerCase() === search.trim().toLowerCase());

  return (
    <div className="grid grid-cols-[140px_1fr] items-center gap-3 py-1.5 group">
      <span className="text-xs text-muted-foreground font-medium flex items-center gap-1.5 truncate">
        {Icon && <Icon className="w-3.5 h-3.5 shrink-0 text-muted-foreground/60" />}
        {label}
      </span>
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <button
            type="button"
            className="flex items-center gap-1.5 min-w-0 cursor-pointer rounded-md px-2 py-1 -mx-2 hover:bg-seek/5 transition-colors w-full text-start"
          >
            <span className="text-sm text-foreground truncate flex-1">{value || '—'}</span>
            <ChevronsUpDown className="w-3 h-3 text-muted-foreground/40 shrink-0" />
          </button>
        </PopoverTrigger>
        <PopoverContent className="w-[240px] p-0" align="start">
          <div className="flex items-center border-b px-3 py-2">
            <Search className="me-2 h-4 w-4 shrink-0 opacity-50" />
            <input
              className="flex h-7 w-full bg-transparent text-sm outline-none placeholder:text-muted-foreground"
              placeholder={t('seek.cvDiff.searchNationality')}
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
          </div>
          <div className="max-h-[200px] overflow-y-auto p-1">
            {showFreeText && (
              <button
                type="button"
                className="relative flex w-full items-center rounded-sm px-2 py-1.5 text-sm outline-none hover:bg-accent hover:text-accent-foreground text-primary font-medium"
                onClick={() => {
                  onChange(search.trim());
                  setOpen(false);
                  setSearch('');
                }}
              >
                {t('seek.cvDiff.useTypedValue', { value: search.trim() })}
              </button>
            )}
            {filtered.length === 0 && !showFreeText && (
              <p className="py-4 text-center text-sm text-muted-foreground">{t('seek.cvDiff.noResultsFound')}</p>
            )}
            {filtered.map((nat) => (
              <button
                key={nat}
                type="button"
                className="relative flex w-full items-center rounded-sm px-2 py-1.5 text-sm outline-none hover:bg-accent hover:text-accent-foreground"
                onClick={() => {
                  onChange(nat);
                  setOpen(false);
                  setSearch('');
                }}
              >
                <Check className={`me-2 h-3.5 w-3.5 ${value === nat ? 'opacity-100' : 'opacity-0'}`} />
                {nat}
              </button>
            ))}
          </div>
        </PopoverContent>
      </Popover>
    </div>
  );
}

/* ── Section header ── */

function SectionHeader({ icon: Icon, title, count, confidence, decision, onDecisionChange }: {
  icon: typeof Briefcase;
  title: string;
  count: number;
  confidence?: string;
  decision?: FieldDecision;
  onDecisionChange?: (action: DecisionAction) => void;
}) {
  return (
    <div className="flex items-center gap-2 pb-2 border-b border-border/40 mb-3">
      <div className="w-6 h-6 rounded-md bg-seek/10 flex items-center justify-center">
        <Icon className="w-3.5 h-3.5 text-seek" />
      </div>
      <span className="font-semibold text-sm text-foreground">{title}</span>
      {count > 0 && <Badge variant="secondary" className="text-[10px] h-5 min-w-5 flex items-center justify-center">{count}</Badge>}
      {confidence && <ConfidenceBadge level={confidence} />}
      <div className="flex-1" />
      {decision && onDecisionChange && (
        <DecisionToggle action={decision.action} onChange={onDecisionChange} compact />
      )}
    </div>
  );
}

/* ── Change summary ── */

function ChangeSummary({ decisions, parsedData, currentData }: {
  decisions: ReviewDecisions;
  parsedData: ParsedData;
  currentData: CurrentData;
}) {
  const { t } = useTranslation();
  const accepted: string[] = [];
  const rejected: string[] = [];
  const kept: string[] = [];

  const profileFields: Array<{ key: ProfileDecisionKey; label: string }> = [
    { key: 'headline', label: t('seek.cvDiff.fields.headline') },
    { key: 'professional_summary', label: t('seek.cvDiff.fields.summary') },
    { key: 'current_job_title', label: t('seek.cvDiff.fields.jobTitle') },
    { key: 'city', label: t('seek.cvDiff.fields.city') },
    { key: 'country', label: t('seek.cvDiff.fields.country') },
    { key: 'nationality', label: t('seek.cvDiff.fields.nationality') },
  ];

  for (const f of profileFields) {
    const d = decisions[f.key];
    if (!d) continue;
    if (d.action === 'accept') accepted.push(f.label);
    else if (d.action === 'reject') rejected.push(f.label);
    else kept.push(f.label);
  }

  const expAccepted = (decisions.experiences || []).filter(e => e.action === 'accept').length;
  const expRejected = (decisions.experiences || []).filter(e => e.action === 'reject').length;
  if (expAccepted > 0) accepted.push(t('seek.cvDiff.summary.experiencesCount', { count: expAccepted }));
  if (expRejected > 0) rejected.push(t('seek.cvDiff.summary.experiencesCount', { count: expRejected }));

  const eduAccepted = (decisions.education || []).filter(e => e.action === 'accept').length;
  const eduRejected = (decisions.education || []).filter(e => e.action === 'reject').length;
  if (eduAccepted > 0) accepted.push(t('seek.cvDiff.summary.educationCount', { count: eduAccepted }));
  if (eduRejected > 0) rejected.push(t('seek.cvDiff.summary.educationCount', { count: eduRejected }));

  if (decisions.skills?.action === 'accept') accepted.push(t('seek.cvDiff.sections.skills'));
  if (decisions.skills?.action === 'reject') rejected.push(t('seek.cvDiff.sections.skills'));
  if (decisions.languages?.action === 'accept') accepted.push(t('seek.cvDiff.sections.languages'));
  if (decisions.languages?.action === 'reject') rejected.push(t('seek.cvDiff.sections.languages'));
  if (decisions.certifications?.action === 'accept') accepted.push(t('seek.cvDiff.sections.certifications'));
  if (decisions.certifications?.action === 'reject') rejected.push(t('seek.cvDiff.sections.certifications'));

  const addAccepted = (decisions.additional_sections || []).filter(s => s.action === 'accept').length;
  const addRejected = (decisions.additional_sections || []).filter(s => s.action === 'reject').length;
  if (addAccepted > 0) accepted.push(t('seek.cvDiff.summary.additionalSectionsCount', { count: addAccepted }));
  if (addRejected > 0) rejected.push(t('seek.cvDiff.summary.additionalSectionsCount', { count: addRejected }));

  return (
    <div className="rounded-xl border border-seek/20 bg-seek/5 p-4 space-y-3">
      <div className="flex items-center gap-2 text-sm font-semibold text-foreground">
        <ListChecks className="w-4 h-4 text-seek" />
        {t('seek.cvDiff.summary.title')}
      </div>
      {accepted.length > 0 && (
        <div>
          <p className="text-[10px] font-semibold text-seek uppercase tracking-wider mb-1">{t('seek.cvDiff.summary.willBeApplied', { count: accepted.length })}</p>
          <div className="flex flex-wrap gap-1">
            {accepted.map((a, i) => (
              <Badge key={i} variant="outline" className="text-[10px] bg-seek/10 text-seek border-seek/20">{a}</Badge>
            ))}
          </div>
        </div>
      )}
      {kept.length > 0 && (
        <div>
          <p className="text-[10px] font-semibold text-blue-600 dark:text-blue-400 uppercase tracking-wider mb-1">{t('seek.cvDiff.summary.keepingCurrent', { count: kept.length })}</p>
          <div className="flex flex-wrap gap-1">
            {kept.map((k, i) => (
              <Badge key={i} variant="outline" className="text-[10px] bg-blue-50 dark:bg-blue-950/20 text-blue-600 dark:text-blue-400 border-blue-200">{k}</Badge>
            ))}
          </div>
        </div>
      )}
      {rejected.length > 0 && (
        <div>
          <p className="text-[10px] font-semibold text-destructive uppercase tracking-wider mb-1">{t('seek.cvDiff.summary.rejected', { count: rejected.length })}</p>
          <div className="flex flex-wrap gap-1">
            {rejected.map((r, i) => (
              <Badge key={i} variant="outline" className="text-[10px] bg-destructive/10 text-destructive border-destructive/20">{r}</Badge>
            ))}
          </div>
        </div>
      )}
      {accepted.length === 0 && (
        <p className="text-xs text-muted-foreground">{t('seek.cvDiff.summary.noChangesSelected')}</p>
      )}
    </div>
  );
}

/* ── Main component ── */

export function CvDiffPreview({
  open, onClose, onDiscard, onApply, onFinishNoChanges, applying,
  parsedData, currentData, validationIssues,
  extractionQuality, confidence,
  decisions, onUpdateFieldAction, onUpdateItemAction, onUpdateAdditionalAction,
  isRecoveredSession,
}: Props) {
  const { t } = useTranslation();
  const [showIssues, setShowIssues] = useState(false);
  const [edited, setEdited] = useState<ParsedData>(() => JSON.parse(JSON.stringify(parsedData)));
  const [activeTab, setActiveTab] = useState('overview');
  const warningCount = validationIssues.filter(i => i.severity === 'warning' || i.severity === 'error').length;
  const autoFixedCount = validationIssues.filter(i => i.auto_fixed).length;

  const additionalSections = useMemo(() => {
    return (parsedData?.additional_sections || []).filter((section) => section.quality !== 'junk');
  }, [parsedData]);

  const hasAdditional = additionalSections.length > 0;
  const tabCount = 4 + (hasAdditional ? 1 : 0) + 1; // +1 for summary

  useEffect(() => {
    setEdited(JSON.parse(JSON.stringify(parsedData)));
  }, [parsedData]);

  const updateField = useCallback((field: string, value: string) => {
    setEdited(prev => ({ ...prev, [field]: value }));
  }, []);

  const updateExperience = useCallback((idx: number, field: string, value: string | boolean | null) => {
    setEdited(prev => {
      const exps = [...(prev.experiences || [])];
      exps[idx] = { ...exps[idx], [field]: value };
      return { ...prev, experiences: exps };
    });
  }, []);

  const updateEducation = useCallback((idx: number, field: string, value: string) => {
    setEdited(prev => {
      const edus = [...(prev.education || [])];
      edus[idx] = { ...edus[idx], [field]: value };
      return { ...prev, education: edus };
    });
  }, []);

  // Count accepted items for the apply button
  const acceptedCount = useMemo(() => {
    let count = 0;
    const fields: ProfileDecisionKey[] = ['headline', 'professional_summary', 'current_job_title', 'city', 'country', 'nationality', 'highest_education_level', 'total_years_experience'];
    for (const f of fields) {
      if (decisions[f]?.action === 'accept') count++;
    }
    count += (decisions.experiences || []).filter(e => e.action === 'accept').length;
    count += (decisions.education || []).filter(e => e.action === 'accept').length;
    if (decisions.skills?.action === 'accept') count++;
    if (decisions.languages?.action === 'accept') count++;
    if (decisions.certifications?.action === 'accept') count++;
    count += (decisions.additional_sections || []).filter(s => s.action === 'accept').length;
    return count;
  }, [decisions]);

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v) onClose(); }}>
      <DialogContent className="max-w-[720px] h-[90vh] overflow-hidden flex flex-col p-0 gap-0 rounded-2xl border-border/60 shadow-2xl">

        {/* ── Header ── */}
        <DialogHeader className="px-6 pt-5 pb-4 space-y-3 border-b border-border/50 bg-card/50">
          <div className="flex items-start gap-3">
            <div className="w-10 h-10 rounded-xl bg-seek/10 flex items-center justify-center shrink-0">
              <Shield className="w-5 h-5 text-seek" />
            </div>
            <div className="space-y-1 min-w-0">
              <DialogTitle className="text-base font-semibold">
                {t('seek.cvDiff.title')}
                {isRecoveredSession && (
                  <Badge variant="outline" className="ms-2 text-[10px] bg-amber-50 dark:bg-amber-950/20 text-amber-600 border-amber-200">
                    <RotateCcw className="w-2.5 h-2.5 me-1" /> {t('seek.cvDiff.recovered')}
                  </Badge>
                )}
              </DialogTitle>
              <DialogDescription className="text-xs text-muted-foreground leading-relaxed">
                {t('seek.cvDiff.description')}
              </DialogDescription>
            </div>
          </div>
          <div className="flex flex-wrap items-center gap-1.5">
            <QualityBadge quality={extractionQuality} />
            <Badge variant="outline" className="gap-1 text-[10px] bg-seek/5 text-seek border-seek/15">
              <Pencil className="w-2.5 h-2.5" /> {t('seek.cvDiff.editable')}
            </Badge>
            {autoFixedCount > 0 && (
              <Badge variant="outline" className="gap-1 text-[10px] bg-seek/10 text-seek border-seek/20">
                <CheckCircle2 className="w-2.5 h-2.5" /> {t('seek.cvDiff.autoFixedCount', { count: autoFixedCount })}
              </Badge>
            )}
            {acceptedCount > 0 && (
              <Badge variant="outline" className="gap-1 text-[10px] bg-seek/10 text-seek border-seek/20">
                <ThumbsUp className="w-2.5 h-2.5" /> {t('seek.cvDiff.acceptedCount', { count: acceptedCount })}
              </Badge>
            )}
          </div>
        </DialogHeader>

        {/* ── Validation banner ── */}
        {validationIssues.length > 0 && (
          <div className="px-6 py-2.5 bg-amber-50/60 dark:bg-amber-950/10 border-b border-amber-200/50">
            <button
              onClick={() => setShowIssues(!showIssues)}
              className="w-full flex items-center justify-between text-xs font-medium text-amber-800 dark:text-amber-300"
            >
              <div className="flex items-center gap-1.5">
                <AlertTriangle className="w-3.5 h-3.5" />
                {t('seek.cvDiff.validationSummary', { notes: warningCount, fixed: autoFixedCount })}
              </div>
              {showIssues ? <ChevronUp className="w-3.5 h-3.5" /> : <ChevronDown className="w-3.5 h-3.5" />}
            </button>
            {showIssues && (
              <div className="mt-2 space-y-1.5 text-[11px]">
                {validationIssues.map((issue, i) => (
                  <div key={i} className="flex items-start gap-1.5">
                    {issue.severity === 'error' ? <AlertTriangle className="w-3 h-3 text-destructive shrink-0 mt-px" /> :
                     issue.severity === 'warning' ? <AlertTriangle className="w-3 h-3 text-amber-500 shrink-0 mt-px" /> :
                     <Info className="w-3 h-3 text-blue-500 shrink-0 mt-px" />}
                    <span className="text-muted-foreground">
                      {issue.issue}
                      {issue.auto_fixed && <span className="text-seek ms-1">{t('seek.cvDiff.fixed')}</span>}
                    </span>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {/* ── Tab navigation + scrollable content ── */}
        <div className="flex-1 min-h-0 flex flex-col">
          <Tabs value={activeTab} onValueChange={setActiveTab} className="flex-1 min-h-0 flex flex-col">
            <div className="px-6 pt-3 pb-0 bg-background/80 backdrop-blur-sm sticky top-0 z-10">
              <TabsList className={`w-full grid h-9 bg-muted/60 ${hasAdditional ? 'grid-cols-6' : 'grid-cols-5'}`}>
                <TabsTrigger value="overview" className="text-xs gap-1 data-[state=active]:bg-background data-[state=active]:shadow-sm">
                  <User className="w-3 h-3" /> {t('seek.cvDiff.tabs.overview')}
                </TabsTrigger>
                <TabsTrigger value="experience" className="text-xs gap-1 data-[state=active]:bg-background data-[state=active]:shadow-sm">
                  <Briefcase className="w-3 h-3" /> {t('seek.cvDiff.tabs.experience')}
                </TabsTrigger>
                <TabsTrigger value="education" className="text-xs gap-1 data-[state=active]:bg-background data-[state=active]:shadow-sm">
                  <GraduationCap className="w-3 h-3" /> {t('seek.cvDiff.tabs.education')}
                </TabsTrigger>
                <TabsTrigger value="skills" className="text-xs gap-1 data-[state=active]:bg-background data-[state=active]:shadow-sm">
                  <Wrench className="w-3 h-3" /> {t('seek.cvDiff.tabs.skills')}
                </TabsTrigger>
                {hasAdditional && (
                  <TabsTrigger value="additional" className="text-xs gap-1 data-[state=active]:bg-background data-[state=active]:shadow-sm">
                    <FileQuestion className="w-3 h-3" /> {t('seek.cvDiff.tabs.more')}
                  </TabsTrigger>
                )}
                <TabsTrigger value="summary" className="text-xs gap-1 data-[state=active]:bg-background data-[state=active]:shadow-sm">
                  <ListChecks className="w-3 h-3" /> {t('seek.cvDiff.tabs.summary')}
                </TabsTrigger>
              </TabsList>
            </div>

            <div className="flex-1 min-h-0 overflow-y-auto px-6 pt-4 pb-6">
              {/* ── Overview ── */}
              <TabsContent value="overview" className="mt-0 space-y-5">
                <SectionHeader icon={User} title={t('seek.cvDiff.sections.personalInformation')} count={0} confidence={confidence?.personal_info} />
                <div className="rounded-xl border border-border/40 bg-card/30 p-4 space-y-2">
                  <EditableFieldWithDecision
                    icon={Briefcase} label={t('seek.cvDiff.fields.jobTitle')}
                    cvValue={edited.current_job_title || ''}
                    currentValue={currentData.profile?.current_job_title}
                    onChange={(v) => updateField('current_job_title', v)}
                    decision={decisions.current_job_title}
                    onDecisionChange={(a) => onUpdateFieldAction('current_job_title', a)}
                  />
                  <EditableFieldWithDecision
                    icon={User} label={t('seek.cvDiff.fields.headline')}
                    cvValue={edited.headline || ''}
                    currentValue={currentData.profile?.headline}
                    onChange={(v) => updateField('headline', v)}
                    decision={decisions.headline}
                    onDecisionChange={(a) => onUpdateFieldAction('headline', a)}
                  />
                  <Separator className="my-2 opacity-40" />
                  <EditableFieldWithDecision
                    icon={MapPin} label={t('seek.cvDiff.fields.city')}
                    cvValue={edited.city || ''}
                    currentValue={currentData.profile?.city}
                    onChange={(v) => updateField('city', v)}
                    decision={decisions.city}
                    onDecisionChange={(a) => onUpdateFieldAction('city', a)}
                  />
                  <EditableFieldWithDecision
                    icon={Globe} label={t('seek.cvDiff.fields.country')}
                    cvValue={edited.country || ''}
                    currentValue={currentData.profile?.country}
                    onChange={(v) => updateField('country', v)}
                    decision={decisions.country}
                    onDecisionChange={(a) => onUpdateFieldAction('country', a)}
                  />
                  <EditableNationalityField icon={Flag} label={t('seek.cvDiff.fields.nationality')} value={edited.nationality || ''} onChange={(v) => updateField('nationality', v)} />
                  <Separator className="my-2 opacity-40" />
                  <EditableFieldWithDecision
                    icon={BookOpen} label={t('seek.cvDiff.fields.educationLevel')}
                    cvValue={edited.highest_education_level || ''}
                    currentValue={currentData.profile?.highest_education_level}
                    onChange={(v) => updateField('highest_education_level', v)}
                    decision={decisions.highest_education_level}
                    onDecisionChange={(a) => onUpdateFieldAction('highest_education_level', a)}
                  />
                  <EditableFieldWithDecision
                    icon={Clock} label={t('seek.cvDiff.fields.yearsExperience')}
                    cvValue={edited.total_years_experience?.toString() || ''}
                    currentValue={currentData.profile?.total_years_experience?.toString()}
                    onChange={(v) => updateField('total_years_experience', v)}
                    decision={decisions.total_years_experience}
                    onDecisionChange={(a) => onUpdateFieldAction('total_years_experience', a)}
                  />
                </div>

                {(edited.professional_summary || edited.professional_summary === '') && (
                  <div>
                    <div className="flex items-center justify-between mb-2">
                      <p className="text-[11px] font-semibold text-muted-foreground uppercase tracking-wider">{t('seek.cvDiff.fields.professionalSummary')}</p>
                      {decisions.professional_summary && (
                        <DecisionToggle
                          action={decisions.professional_summary.action}
                          onChange={(a) => onUpdateFieldAction('professional_summary', a)}
                          compact
                        />
                      )}
                    </div>
                    {decisions.professional_summary?.action === 'keep_current' && currentData.profile?.professional_summary ? (
                      <div className="text-sm text-foreground/80 leading-relaxed bg-blue-50 dark:bg-blue-950/20 rounded-lg p-3 border border-blue-200/50">
                        <span className="text-[10px] text-blue-600 dark:text-blue-400 font-medium block mb-1">{t('seek.cvDiff.currentProfileSummary')}</span>
                        {currentData.profile.professional_summary}
                      </div>
                    ) : decisions.professional_summary?.action === 'reject' ? (
                      <div className="text-sm text-muted-foreground line-through bg-muted/20 rounded-lg p-3 border border-border/30">
                        {edited.professional_summary}
                      </div>
                    ) : (
                      <EditableSummary value={edited.professional_summary || ''} onChange={(v) => updateField('professional_summary', v)} />
                    )}
                  </div>
                )}
              </TabsContent>

              {/* ── Experience ── */}
              <TabsContent value="experience" className="mt-0 space-y-4">
                <SectionHeader icon={Briefcase} title={t('seek.cvDiff.sections.workExperience')} count={edited.experiences?.length || 0} confidence={confidence?.experiences} />
                {edited.experiences?.map((exp, i) => {
                  const itemDecision = decisions.experiences?.find(d => d.index === i);
                  const isRejected = itemDecision?.action === 'reject';
                  return (
                    <div key={i} className={`rounded-xl border p-4 space-y-1 transition-opacity ${
                      isRejected ? 'border-destructive/20 bg-destructive/5 opacity-60' : 'border-border/40 bg-card/30'
                    }`}>
                      <div className="flex items-center justify-between mb-1">
                        <div className="flex items-center gap-2">
                          <Badge variant="outline" className="text-[10px] text-muted-foreground font-normal">#{i + 1}</Badge>
                          {exp.employment_type && <Badge variant="secondary" className="text-[10px]">{exp.employment_type}</Badge>}
                        </div>
                        {itemDecision && (
                          <DecisionToggle
                            action={itemDecision.action}
                            onChange={(a) => onUpdateItemAction('experiences', i, a)}
                            compact
                          />
                        )}
                      </div>
                      {!isRejected && (
                        <>
                          <EditableField icon={Briefcase} label={t('seek.cvDiff.fields.title')} value={exp.title} onChange={(v) => updateExperience(i, 'title', v)} />
                          <EditableField icon={User} label={t('seek.cvDiff.fields.employer')} value={exp.employer_name || ''} onChange={(v) => updateExperience(i, 'employer_name', v)} />
                          <EditableField icon={MapPin} label={t('seek.cvDiff.fields.location')} value={exp.location || ''} onChange={(v) => updateExperience(i, 'location', v)} />
                          <Separator className="my-1.5 opacity-30" />
                          <div className="grid grid-cols-2 gap-2">
                            <div className="space-y-1">
                              <span className="text-xs text-muted-foreground font-medium flex items-center gap-1.5"><Clock className="w-3.5 h-3.5 text-muted-foreground/60" />{t('seek.cvDiff.fields.start')}</span>
                              <MonthYearPicker value={exp.start_date?.substring(0, 7)} onChange={(v) => updateExperience(i, 'start_date', v)} />
                            </div>
                            <div className="space-y-1">
                              <span className="text-xs text-muted-foreground font-medium flex items-center gap-1.5"><Clock className="w-3.5 h-3.5 text-muted-foreground/60" />{t('seek.cvDiff.fields.end')}</span>
                              <MonthYearPicker
                                value={exp.end_date?.substring(0, 7)}
                                onChange={(v) => updateExperience(i, 'end_date', v)}
                                showPresent
                                isPresent={!!exp.is_current}
                                onPresentChange={(p) => {
                                  updateExperience(i, 'is_current', p);
                                  if (p) updateExperience(i, 'end_date', null);
                                }}
                              />
                            </div>
                          </div>
                          {(exp.description || exp.description === '') && (
                            <div className="mt-2">
                              <EditableSummary value={exp.description || ''} onChange={(v) => updateExperience(i, 'description', v)} />
                            </div>
                          )}
                        </>
                      )}
                      {isRejected && (
                        <p className="text-xs text-muted-foreground py-2">
                          {t('seek.cvDiff.experienceRejected', {
                            title: exp.title,
                            employer: exp.employer_name || t('seek.cvDiff.unknown'),
                          })}
                        </p>
                      )}
                    </div>
                  );
                })}
                {(!edited.experiences || edited.experiences.length === 0) && (
                  <p className="text-sm text-muted-foreground text-center py-8">{t('seek.cvDiff.noExperiencesExtracted')}</p>
                )}
              </TabsContent>

              {/* ── Education ── */}
              <TabsContent value="education" className="mt-0 space-y-4">
                <SectionHeader icon={GraduationCap} title={t('seek.cvDiff.sections.education')} count={edited.education?.length || 0} confidence={confidence?.education} />
                {edited.education?.map((edu, i) => {
                  const itemDecision = decisions.education?.find(d => d.index === i);
                  const isRejected = itemDecision?.action === 'reject';
                  return (
                    <div key={i} className={`rounded-xl border p-4 space-y-1 transition-opacity ${
                      isRejected ? 'border-destructive/20 bg-destructive/5 opacity-60' : 'border-border/40 bg-card/30'
                    }`}>
                      <div className="flex items-center justify-between mb-1">
                        <Badge variant="outline" className="text-[10px] text-muted-foreground font-normal">#{i + 1}</Badge>
                        {itemDecision && (
                          <DecisionToggle
                            action={itemDecision.action}
                            onChange={(a) => onUpdateItemAction('education', i, a)}
                            compact
                          />
                        )}
                      </div>
                      {!isRejected ? (
                        <>
                          <EditableField icon={GraduationCap} label={t('seek.cvDiff.fields.institution')} value={edu.institution_name} onChange={(v) => updateEducation(i, 'institution_name', v)} />
                          <EditableField icon={BookOpen} label={t('seek.cvDiff.fields.degree')} value={edu.degree || ''} onChange={(v) => updateEducation(i, 'degree', v)} />
                          <EditableField icon={Wrench} label={t('seek.cvDiff.fields.fieldOfStudy')} value={edu.field_of_study || ''} onChange={(v) => updateEducation(i, 'field_of_study', v)} />
                          <Separator className="my-1.5 opacity-30" />
                          <div className="grid grid-cols-2 gap-2">
                            <div className="space-y-1">
                              <span className="text-xs text-muted-foreground font-medium flex items-center gap-1.5"><Clock className="w-3.5 h-3.5 text-muted-foreground/60" />{t('seek.cvDiff.fields.start')}</span>
                              <MonthYearPicker value={edu.start_date?.substring(0, 7)} onChange={(v) => updateEducation(i, 'start_date', v)} />
                            </div>
                            <div className="space-y-1">
                              <span className="text-xs text-muted-foreground font-medium flex items-center gap-1.5"><Clock className="w-3.5 h-3.5 text-muted-foreground/60" />{t('seek.cvDiff.fields.end')}</span>
                              <MonthYearPicker value={edu.end_date?.substring(0, 7)} onChange={(v) => updateEducation(i, 'end_date', v)} />
                            </div>
                          </div>
                        </>
                      ) : (
                        <p className="text-xs text-muted-foreground py-2">
                          {t('seek.cvDiff.educationRejected', {
                            institution: edu.institution_name,
                            degree: edu.degree || t('seek.cvDiff.unknownDegree'),
                          })}
                        </p>
                      )}
                    </div>
                  );
                })}

                {(edited.certifications?.length || 0) > 0 && (
                  <>
                    <div className="pt-2" />
                    <SectionHeader
                      icon={Award}
                      title={t('seek.cvDiff.sections.certifications')}
                      count={edited.certifications?.length || 0}
                      confidence={confidence?.certifications}
                      decision={decisions.certifications}
                      onDecisionChange={(a) => onUpdateFieldAction('certifications', a)}
                    />
                    <div className={`rounded-xl border p-4 space-y-2 transition-opacity ${
                      decisions.certifications?.action === 'reject' ? 'border-destructive/20 bg-destructive/5 opacity-60' : 'border-border/40 bg-card/30'
                    }`}>
                      {edited.certifications?.map((cert, i) => (
                        <div key={i} className="flex items-center gap-2 text-sm py-1.5">
                          <div className="w-5 h-5 rounded-md bg-amber-500/10 flex items-center justify-center shrink-0">
                            <Award className="w-3 h-3 text-amber-600" />
                          </div>
                          <span className={`font-medium ${decisions.certifications?.action === 'reject' ? 'line-through text-muted-foreground' : 'text-foreground'}`}>{cert.certification_name}</span>
                          {cert.issuing_organization && (
                            <span className="text-muted-foreground text-xs">· {cert.issuing_organization}</span>
                          )}
                        </div>
                      ))}
                    </div>
                  </>
                )}
              </TabsContent>

              {/* ── Skills & Languages ── */}
              <TabsContent value="skills" className="mt-0 space-y-5">
                {(() => {
                  const skillsObj = edited.skills;
                  const flatSkills = Array.isArray(skillsObj) ? skillsObj : (skillsObj?.skills ?? []);
                  const expertise = !Array.isArray(skillsObj) ? (skillsObj?.expertise ?? []) : [];
                  const keyStrengths = !Array.isArray(skillsObj) ? (skillsObj?.key_strengths ?? []) : [];
                  const totalCount = flatSkills.length + expertise.length + keyStrengths.length;
                  return (
                    <>
                      <SectionHeader
                        icon={Wrench}
                        title={t('seek.cvDiff.sections.skills')}
                        count={totalCount}
                        confidence={confidence?.skills}
                        decision={decisions.skills}
                        onDecisionChange={(a) => onUpdateFieldAction('skills', a)}
                      />
                      <div className={`rounded-xl border p-4 space-y-4 transition-opacity ${
                        decisions.skills?.action === 'reject' ? 'border-destructive/20 bg-destructive/5 opacity-60' : 'border-border/40 bg-card/30'
                      }`}>
                        {flatSkills.length > 0 && (
                          <div>
                            <p className="text-[11px] font-semibold text-muted-foreground uppercase tracking-wider mb-2">{t('seek.cvDiff.sections.technicalSkills')}</p>
                            <div className="flex flex-wrap gap-1.5">
                              {flatSkills.map((skill: string, i: number) => (
                                <Badge key={i} variant="secondary" className={`text-xs font-normal ${decisions.skills?.action === 'reject' ? 'line-through' : ''}`}>{skill}</Badge>
                              ))}
                            </div>
                          </div>
                        )}
                        {expertise.length > 0 && (
                          <div>
                            <p className="text-[11px] font-semibold text-muted-foreground uppercase tracking-wider mb-2">{t('seek.cvDiff.sections.expertise')}</p>
                            <div className="flex flex-wrap gap-1.5">
                              {expertise.map((s: string, i: number) => (
                                <Badge key={i} variant="outline" className={`text-xs font-normal ${decisions.skills?.action === 'reject' ? 'line-through' : ''}`}>{s}</Badge>
                              ))}
                            </div>
                          </div>
                        )}
                        {keyStrengths.length > 0 && (
                          <div>
                            <p className="text-[11px] font-semibold text-muted-foreground uppercase tracking-wider mb-2">{t('seek.cvDiff.sections.keyStrengths')}</p>
                            <div className="flex flex-wrap gap-1.5">
                              {keyStrengths.map((s: string, i: number) => (
                                <Badge key={i} variant="outline" className={`text-xs font-normal border-seek/30 text-seek ${decisions.skills?.action === 'reject' ? 'line-through' : ''}`}>{s}</Badge>
                              ))}
                            </div>
                          </div>
                        )}
                        {totalCount === 0 && <p className="text-sm text-muted-foreground text-center py-4">{t('seek.cvDiff.noSkillsExtracted')}</p>}
                      </div>
                    </>
                  );
                })()}

                <SectionHeader
                  icon={Globe}
                  title={t('seek.cvDiff.sections.languages')}
                  count={edited.languages?.length || 0}
                  confidence={confidence?.languages}
                  decision={decisions.languages}
                  onDecisionChange={(a) => onUpdateFieldAction('languages', a)}
                />
                {(edited.languages?.length || 0) > 0 && (
                  <div className={`rounded-xl border p-4 space-y-2 transition-opacity ${
                    decisions.languages?.action === 'reject' ? 'border-destructive/20 bg-destructive/5 opacity-60' : 'border-border/40 bg-card/30'
                  }`}>
                    {edited.languages?.map((lang, i) => (
                      <div key={i} className="flex items-center gap-2 text-sm py-1">
                        <div className="w-5 h-5 rounded-md bg-blue-500/10 flex items-center justify-center shrink-0">
                          <Globe className="w-3 h-3 text-blue-600" />
                        </div>
                        <span className={`font-medium ${decisions.languages?.action === 'reject' ? 'line-through text-muted-foreground' : ''}`}>{lang.language_name}</span>
                        {lang.proficiency_level && <Badge variant="outline" className="text-[10px] font-normal">{lang.proficiency_level}</Badge>}
                      </div>
                    ))}
                  </div>
                )}
              </TabsContent>

              {/* ── Additional Sections (unmapped content) ── */}
              {hasAdditional && (
                <TabsContent value="additional" className="mt-0 space-y-4">
                  <SectionHeader icon={FileQuestion} title={t('seek.cvDiff.sections.additionalSections')} count={additionalSections.length} />
                  <p className="text-xs text-muted-foreground">
                    {t('seek.cvDiff.additionalSectionsDesc')}
                  </p>
                  {additionalSections.map((section) => {
                    const sectionDecision = decisions.additional_sections?.find(d => d.stable_id === section.stable_id);
                    const isRejected = sectionDecision?.action === 'reject';
                    return (
                      <div key={section.stable_id} className={`rounded-xl border p-4 transition-opacity ${
                        isRejected ? 'border-destructive/20 bg-destructive/5 opacity-60' : 'border-border/40 bg-card/30'
                      }`}>
                        <div className="flex items-center justify-between mb-2">
                          <div className="flex items-center gap-2">
                            <Badge variant="outline" className="text-[10px]">{section.category}</Badge>
                            <span className="font-medium text-sm text-foreground">{section.section_label}</span>
                          </div>
                          {sectionDecision && (
                            <DecisionToggle
                              action={sectionDecision.action}
                              onChange={(a) => onUpdateAdditionalAction(section.stable_id, a)}
                              compact
                            />
                          )}
                        </div>
                        <p className={`text-sm leading-relaxed ${isRejected ? 'line-through text-muted-foreground' : 'text-foreground/80'}`}>
                          {section.content}
                        </p>
                        <div className="flex items-center gap-2 mt-2">
                          <Badge variant="outline" className={`text-[10px] ${
                            section.confidence >= 0.85 ? 'bg-seek/10 text-seek border-seek/20' :
                            section.confidence >= 0.5 ? 'bg-amber-500/10 text-amber-700 border-amber-200' :
                            'bg-red-500/10 text-red-700 border-red-200'
                          }`}>
                            {t('seek.cvDiff.confidencePercent', { percent: Math.round(section.confidence * 100) })}
                          </Badge>
                        </div>
                      </div>
                    );
                  })}
                </TabsContent>
              )}

              {/* ── Summary ── */}
              <TabsContent value="summary" className="mt-0 space-y-4">
                <ChangeSummary decisions={decisions} parsedData={parsedData} currentData={currentData} />
              </TabsContent>
            </div>
          </Tabs>
        </div>

        {/* ── Footer ── */}
        <DialogFooter className="px-6 py-3.5 border-t border-border/50 bg-muted/20">
          <div className="flex items-center justify-between w-full">
            <Button
              variant="ghost"
              size="sm"
              onClick={onDiscard}
              disabled={applying}
              className="text-destructive hover:text-destructive hover:bg-destructive/10 text-xs h-8"
            >
              {t('seek.cvDiff.discard')}
            </Button>
            <div className="flex gap-2">
              <Button variant="outline" size="sm" onClick={onClose} disabled={applying} className="h-8 text-xs">
                {t('seek.cvDiff.minimize')}
              </Button>
              {activeTab === 'summary' && acceptedCount === 0 && onFinishNoChanges && (
                <Button
                  variant="outline"
                  size="sm"
                  onClick={onFinishNoChanges}
                  disabled={applying}
                  className="h-8 text-xs gap-1.5"
                >
                  <CheckCircle2 className="w-3.5 h-3.5" />
                  {t('seek.cvDiff.finishWithoutChanges')}
                </Button>
              )}
              <Button
                size="sm"
                onClick={() => {
                  if (activeTab !== 'summary') {
                    setActiveTab('summary');
                    return;
                  }
                  onApply(edited, decisions);
                }}
                disabled={applying || (activeTab === 'summary' && acceptedCount === 0)}
                className="bg-seek hover:bg-seek/90 text-seek-foreground gap-1.5 h-8 text-xs px-4"
              >
                {applying ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : activeTab === 'summary' ? <CheckCircle2 className="w-3.5 h-3.5" /> : <ListChecks className="w-3.5 h-3.5" />}
                {applying ? t('seek.cvDiff.applying') : activeTab === 'summary' ? t('seek.cvDiff.applyChanges', { count: acceptedCount }) : t('seek.cvDiff.reviewSummary')}
              </Button>
            </div>
          </div>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

/* ── Simple editable field (no decision toggle) ── */

function EditableField({ label, value, onChange, icon: Icon }: {
  label: string;
  value: string;
  onChange: (v: string) => void;
  icon?: typeof Briefcase;
}) {
  const [editing, setEditing] = useState(false);
  if (!value && value !== '') return null;

  return (
    <div className="grid grid-cols-[140px_1fr] items-center gap-3 py-1.5 group">
      <span className="text-xs text-muted-foreground font-medium flex items-center gap-1.5 truncate">
        {Icon && <Icon className="w-3.5 h-3.5 shrink-0 text-muted-foreground/60" />}
        {label}
      </span>
      {editing ? (
        <Input
          value={value}
          onChange={(e) => onChange(e.target.value)}
          onBlur={() => setEditing(false)}
          onKeyDown={(e) => e.key === 'Enter' && setEditing(false)}
          className="h-8 text-sm"
          autoFocus
        />
      ) : (
        <div
          className="flex items-center gap-1.5 min-w-0 cursor-pointer rounded-md px-2 py-1 -mx-2 hover:bg-seek/5 transition-colors"
          onClick={() => setEditing(true)}
        >
          <span className="text-sm text-foreground truncate">{value || '—'}</span>
          <Pencil className="w-3 h-3 text-muted-foreground/30 opacity-0 group-hover:opacity-100 shrink-0 transition-opacity" />
        </div>
      )}
    </div>
  );
}

/* ── Editable summary block ── */

function EditableSummary({ value, onChange }: { value: string; onChange: (v: string) => void }) {
  const { t } = useTranslation();
  const [editing, setEditing] = useState(false);

  if (editing) {
    return (
      <Textarea
        value={value}
        onChange={(e) => onChange(e.target.value)}
        onBlur={() => setEditing(false)}
        className="text-sm leading-relaxed min-h-[100px] rounded-lg"
        autoFocus
      />
    );
  }

  return (
    <div
      className="text-sm text-foreground/80 leading-relaxed bg-muted/20 rounded-lg p-3 border border-border/30 whitespace-pre-line cursor-pointer hover:bg-seek/5 hover:border-seek/20 transition-colors group relative"
      onClick={() => setEditing(true)}
    >
      {value || <span className="text-muted-foreground italic">{t('seek.cvDiff.noSummaryProvided')}</span>}
      <Pencil className="w-3.5 h-3.5 text-muted-foreground/30 absolute top-2.5 end-2.5 opacity-0 group-hover:opacity-100 transition-opacity" />
    </div>
  );
}
